import { colors } from '@/theme/foundations/colors';
import {
  NON_DIGIT_GLOBAL_REGEX,
  THOUSANDS_SEPARATOR_REGEX,
} from '@/constants/regex';
import { KText } from '@/ui-library/components/text';
import { Box, Input, Flex, Icon, InputGroup } from '@chakra-ui/react';
import { Copy03 } from '@kula-ai/icons/dual-tone/Copy03';
import { useField } from 'formik';
import { useRef, useState } from 'react';
import { currencyFormatterHelpers } from '@/utils/currencyFormatter';
import {
  handleNumberInputChange,
  handleNumberKeyDown as validateNumberKeyDown,
  handleNumberPaste as validateNumberPaste,
} from './numberFieldValidation';
import { MaxLengthCounter } from '../MaxLengthCounter';
const { formatCurrencyNumber, cleanCurrencyInput } = currencyFormatterHelpers;

interface InputFieldProps {
  item?: Record<string, unknown>;
  field: any;
  formik?: any;
}

export const InputField = ({ item = {}, field, formik }: InputFieldProps) => {
  const {
    name,
    inputType,
    isEditable = true,
    placeholder = '',
    placeholderStyles = {}, // merged into the input's `_placeholder` style
    label,
    style,
    maxLength,
    disabled,
    showCopy,
    CustomLeftElement,
    CustomRightElement,
    inputRef,
    showErrorStyles = false,
    isRequired,
    formatType,
    currencyFieldName,
    minValue = 0,
    maxValue,
    stepValue,
    isInputSubmit = false,
    customValidation,
    AfterInputGroupContent,
    // When true, render AfterInputGroupContent beside the input (in a flex row)
    // instead of stacked below it.
    inlineAfterInputGroupContent = false,
    customOnChange = () => {},
    customOnKeyDown = () => {},
    beforeMaxLength,
    afterMaxLength = true,
    debounceOnType = () => {},
    autoFocus = false,
    onPaste,
  } = item as any;

  const [tooltipMessage, setTooltipMessage] = useState('');
  const [_field, meta, helpers] = useField(name);
  const { setTouched, setValue } = helpers;
  const { submitCount } = formik || {};
  const { value, touched } = meta || {};
  const tooltipTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [, currencyMeta] = useField(
    currencyFieldName || 'dummy_currency_field'
  );
  const selectedCurrencyId = currencyFieldName ? currencyMeta?.value : null;

  const intervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const localInputRef = useRef<HTMLInputElement | null>(null);

  if (!item) return null;

  const showError =
    (submitCount > 0 || touched) &&
    (!(value?.toString()?.length > 0) || meta?.error) &&
    !showErrorStyles &&
    isRequired;

  const defaultStyles = {
    fontSize: '14px',
    paddingInlineStart: '.75em',
    p: '14px 10px',
    height: '44px',
    color: colors['secondary-700'],
    backgroundColor: !isEditable && '#F7F7F7',
  };

  const handleCopy = () => {
    const inputValue = field.value || '';
    navigator.clipboard.writeText(inputValue);
  };

  const formatNumberWithCommas = (value) => {
    if (!value) return '';

    const numericValue = (value?.toString() || '')
      .split('.')[0]
      .replace(NON_DIGIT_GLOBAL_REGEX, '');

    return numericValue.replace(THOUSANDS_SEPARATOR_REGEX, ',');
  };

  const getSelectedCurrencyString = () => {
    const currencies = (
      item as { currencies?: Array<{ id: unknown; currency?: string }> }
    ).currencies;
    if (!currencyFieldName || !selectedCurrencyId || !currencies) return null;
    const selectedCurrency = currencies.find(
      (c) => c.id === selectedCurrencyId
    );
    return selectedCurrency?.currency || null;
  };

  const formattedValue = (() => {
    // Use `??` so the number 0 isn't coerced to '' and erased from the input.
    const rawValue = field?.value ?? '';
    if (formatType === 'currency') {
      const currencyString = getSelectedCurrencyString();
      if (currencyString && currencyFieldName) {
        return formatCurrencyNumber(rawValue, currencyString);
      }
      return formatNumberWithCommas(rawValue);
    }
    return rawValue;
  })();

  const handleOnChange = (event) => {
    clearTimeout(intervalRef.current);
    const value = event.target.value;
    customOnChange(event);

    intervalRef.current = setTimeout(() => {
      debounceOnType(value);
    }, 1000);

    if (customValidation) {
      customValidation(value, event, field.onChange);
    } else if (inputType === 'number') {
      handleNumberInputChange(value, setValue, triggerPasteTooltip);
    } else if (formatType !== 'currency') {
      field.onChange(event);
    } else {
      const cleanedValue = cleanCurrencyInput(value);
      setValue(cleanedValue);
    }
  };

  const triggerPasteTooltip = (message) => {
    setTooltipMessage(message);
    clearTimeout(tooltipTimeoutRef.current);
    tooltipTimeoutRef.current = setTimeout(() => {
      setTooltipMessage('');
    }, 2000);
  };

  const handleNumberKeyDown = (e) => {
    validateNumberKeyDown(e, {
      stepValue,
      minValue,
      maxValue,
      setValue,
    });
  };

  const onPasteHandler = (event) => {
    if (inputType === 'number') validateNumberPaste(event, triggerPasteTooltip);
    onPaste?.(event, setValue);
  };

  const isDisabled = !!(disabled || !isEditable);

  const inlineAfterContent =
    inlineAfterInputGroupContent && !!AfterInputGroupContent;

  return (
    <Box
      position="relative"
      w="100%"
      {...(inlineAfterContent
        ? { display: 'flex', alignItems: 'center', gap: '12px' }
        : {})}
    >
      {/* Floating validator message — absolute so it never reflows the input.
          Replaces the previous Chakra Tooltip whose shouldWrapChildren span
          collapsed the field to its content width when the message opened. */}
      {tooltipMessage && (
        <Box
          position="absolute"
          left="0"
          top="calc(100% + 4px)"
          bg="secondary-950"
          color="white"
          fontSize="14px"
          px="8px"
          py="4px"
          borderRadius="4px"
          whiteSpace="nowrap"
          zIndex={2}
          pointerEvents="none"
        >
          {tooltipMessage}
        </Box>
      )}
      <InputGroup
        w="100%"
        {...(inlineAfterContent ? { flex: '1', minW: 0 } : {})}
        minH="44px"
        alignItems="center"
        transition="background-color 0.15s ease, border-color 0.15s ease"
        bg={
          showError
            ? 'pastel-1-light'
            : isDisabled
              ? 'secondary-25'
              : 'base-white'
        }
        _hover={{
          borderColor: showError ? 'error-200' : 'secondary-300',
          bg: showError
            ? 'pastel-1-light'
            : isDisabled
              ? 'secondary-25'
              : 'secondary-25',
          boxShadow: 'none',
        }}
        _focusWithin={{
          borderColor: showError ? 'error-200' : 'secondary-300',
          bg: showError ? 'pastel-1-light' : 'secondary-25',
          boxShadow: 'none',
        }}
        border="1px solid"
        borderColor={showError ? 'error-200' : 'secondary-100'}
        outlineOffset="0px !important"
        borderRadius="6px"
        {...style}
      >
        {CustomLeftElement && <CustomLeftElement showError={showError} />}
        <Input
          {...field}
          onChange={handleOnChange}
          onClick={() => {
            if (inputRef?.current) {
              inputRef.current?.focus();
            } else if (localInputRef?.current) {
              localInputRef.current?.focus();
            }
          }}
          background="transparent"
          color={showError ? colors['error-200'] : colors['secondary-700']}
          ref={inputRef || localInputRef}
          id={name}
          variant="outline"
          autoComplete="off"
          _placeholder={{
            opacity: 1,
            fontSize: '14px',
            fontWeight: 'medium',
            color: showError ? 'error-100' : 'secondary-500',
            ...placeholderStyles,
          }}
          _hover={{
            outline: 'none !important',
            border: 'none !important',
            boxShadow: 'none !important',
          }}
          _focus={{
            outline: 'none !important',
            border: 'none !important',
            boxShadow: 'none !important',
          }}
          _invalid={{
            border: 'none !important',
            boxShadow: 'none !important',
            background: 'transparent',
          }}
          type={inputType !== 'number' ? inputType || 'text' : undefined}
          inputMode={inputType === 'number' ? 'decimal' : undefined}
          disabled={disabled || !isEditable}
          value={formattedValue}
          placeholder={
            placeholder || `Enter ${label ? label?.toLowerCase() : ''}`
          }
          maxLength={maxLength}
          min={minValue}
          max={maxValue}
          step={stepValue}
          onFocus={() => setTouched(false)}
          onBlur={() => setTouched(true)}
          onWheel={(e) => {
            if (inputType === 'number') {
              e.currentTarget.blur();
            }
          }}
          onKeyDown={(e) => {
            if (customOnKeyDown) customOnKeyDown(e);
            if (e.key === 'Enter') {
              e.preventDefault();
              if (formik && isInputSubmit) formik.submitForm();
            }
            if (inputType === 'number') handleNumberKeyDown(e);
          }}
          autoFocus={autoFocus}
          onPaste={onPasteHandler}
          {...defaultStyles}
          {...style}
          border="none !important"
          outline="none !important"
          boxShadow="none !important"
        />
        {CustomRightElement && beforeMaxLength && (
          <CustomRightElement showError={showError} />
        )}
        {!!maxLength && (
          <MaxLengthCounter
            currentLength={String(field?.value ?? '').length}
            maxLength={maxLength}
            disabled={disabled}
            showError={showError}
          />
        )}
        {CustomRightElement && afterMaxLength && !beforeMaxLength && (
          <CustomRightElement showError={showError} />
        )}
        {showCopy && (
          <Flex
            gap=".5em"
            m="0 2em"
            alignItems="center"
            onClick={handleCopy}
            opacity={!field?.value ? 0.5 : 1}
            cursor={!field?.value ? 'not-allowed' : 'pointer'}
          >
            <Icon as={Copy03} color="ats-primary" />
            <KText color="primary-500">Copy</KText>
          </Flex>
        )}
      </InputGroup>
      {AfterInputGroupContent &&
        (inlineAfterContent ? (
          <Box flexShrink={0} whiteSpace="nowrap">
            <AfterInputGroupContent />
          </Box>
        ) : (
          <AfterInputGroupContent />
        ))}
    </Box>
  );
};
